import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import RoundSpecialsFilter from './RoundSpecialsFilter';
import { ROUND_SPECIAL_FILTER_FIELD_CONFIGS, ROUND_SPECIAL_FILTER_LEGENDS, ROUND_SPECIAL_QUERY_PARAMS_NAME } from './constants';
import RoundSpecialsTable from './RoundSpecialsTable';
import { getLeaguesMemoized, getTimezone, isFetching } from '../../selectors/common';
import { FETCH_OPTIONS } from '../../constants';
import { getRoundSpecialsLastDate, getRoundSpecialsLastRoundId, getRoundSpecialsRounds } from '../../selectors/roundSpecials';
import { aClearRoundSpecialsState, aGetRoundSpecialRoundsAction } from '../../reducers/roundSpecials';
import { constructRoundSpecialsFilterFromQueryParams, formatRoundSpecialFilterValueToQueryParamsFormat } from './helpers';
import { buildQueryParams } from '../../utils/common';

const INITIAL_FILTER_VALUE = ROUND_SPECIAL_FILTER_FIELD_CONFIGS({ leagues: []}).reduce((acc, curr) => {
  // we need to create copies of the object to avoid mutating filterFieldConfigs
  if (Array.isArray(curr.initialValue)) {
    acc[curr.fieldName] = [...curr.initialValue];
  } else if (typeof curr.initialValue === 'object') {
    acc[curr.fieldName] = { ...curr.initialValue };
  } else {
    acc[curr.fieldName] = curr.initialValue;
  }
  return acc;
}, {});

const RoundSpecials = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const isFetchingRounds = useSelector(isFetching);
  const lastDate = useSelector(getRoundSpecialsLastDate);
  const lastRoundId = useSelector(getRoundSpecialsLastRoundId);
  const leagues = useSelector(getLeaguesMemoized);
  const timezone = useSelector(getTimezone);
  const roundSpecialData = useSelector(getRoundSpecialsRounds);

  const tableScrollableEl = useRef();
  const previousScrollTop = useRef();

  const [filterFieldConfigs] = useState(ROUND_SPECIAL_FILTER_FIELD_CONFIGS({ leagues }));
  const [filterValue, setFilterValue] = useState(INITIAL_FILTER_VALUE);

  const search = () => {
    const formattedFilterValue = formatRoundSpecialFilterValueToQueryParamsFormat(filterValue, timezone);
    const queryParams = buildQueryParams(formattedFilterValue);
    tableScrollableEl.current.scrollTop = 0;
    navigate({
      pathname: location.pathname,
      search: queryParams,
    });
    dispatch(aGetRoundSpecialRoundsAction({
      queryParams,
    }));
  };

  const clear = () => {
    const initialFieldValues = filterFieldConfigs.reduce((acc, curr) => {
      acc[curr.fieldName] = curr.initialValue;
      return acc;
    }, {});
    setFilterValue(initialFieldValues);
    dispatch(aGetRoundSpecialRoundsAction( { queryParams: `?${ROUND_SPECIAL_QUERY_PARAMS_NAME.HIDE_ENDED_ROUNDS}=0`} ));
    navigate({
      pathname: location.pathname,
    });
  };

  const handleChangeFilter = (fieldName, value) => {
    setFilterValue({
      ...filterValue,
      [fieldName]: value,
    });
  };

  const loadMore = useCallback(() => {
    const formattedFilterValue = formatRoundSpecialFilterValueToQueryParamsFormat(filterValue, timezone);
    formattedFilterValue.push(['lastRoundId', lastRoundId]);
    formattedFilterValue.push(['lastDate', lastDate]);
    const queryParams = buildQueryParams(formattedFilterValue);
    
    dispatch(aGetRoundSpecialRoundsAction({
      queryParams,
      loadMore: true
    }));
  }, [dispatch, filterValue, lastDate, lastRoundId, timezone]);
    
  const handleScroll = useCallback(() => {
    if (isFetchingRounds[FETCH_OPTIONS.GET_ROUND_SPECIALS]) {
      previousScrollTop.current = tableScrollableEl.current.scrollTop;
      return;
    }
  
    if (!lastDate || !lastRoundId) {
      previousScrollTop.current = tableScrollableEl.current.scrollTop;
      return;
    }
  
    const tableEl = tableScrollableEl.current;
  
    if (tableEl.scrollHeight <= Math.ceil(tableEl.offsetHeight + tableEl.scrollTop)
        && previousScrollTop.current !== tableEl.scrollTop) {
      loadMore();
    }
    previousScrollTop.current = tableScrollableEl.current.scrollTop;
  }, [isFetchingRounds, lastDate, lastRoundId, loadMore]);
  
  useEffect(function addScrollListener() {
    const element = tableScrollableEl.current;
    if (element) {
      previousScrollTop.current = element.scrollTop;
      element.addEventListener('scroll', handleScroll);
    }

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  
  }, [handleScroll, dispatch]);

  useEffect(function onUnmount() {
    return () => {
      dispatch(aClearRoundSpecialsState());
    };
  }, [dispatch]);

  useEffect(function loadInitialRounds() {
    const queryParamsString = window.location.search;
    if (queryParamsString) {
      const filter = constructRoundSpecialsFilterFromQueryParams(queryParamsString, INITIAL_FILTER_VALUE);
  
      dispatch(aGetRoundSpecialRoundsAction({
        queryParams: queryParamsString,
      }));

      setFilterValue({ ...filter });
      return;
    }
  
    dispatch(aGetRoundSpecialRoundsAction( { queryParams: `?${ROUND_SPECIAL_QUERY_PARAMS_NAME.HIDE_ENDED_ROUNDS}=0`}));
  }, [dispatch]);
      
  return (
    <>
      <RoundSpecialsFilter 
        fields={filterFieldConfigs} 
        legends={ROUND_SPECIAL_FILTER_LEGENDS} 
        onSearch={search} 
        onClear={clear} 
        onChange={handleChangeFilter} 
        filterValue={filterValue} />

      <div className="table-wrapper m-t-30">
        <div ref={tableScrollableEl} className="table-responsive fix-15" id="table-view">
          <RoundSpecialsTable rounds={roundSpecialData} leagues={leagues} timezone={timezone} />
        </div>
      </div>
    </>
  );
};

export default RoundSpecials;